import * as autoPrintReceiptsStorage from "../lib/autoPrintReceiptsStorage";
import * as ccMethods from "../lib/api/creditCardScanner";
import * as discountMethods from "../lib/api/discounts";
import * as errors from "./errors";
import * as logs from "./api/logs";
import * as menuMethods from "../lib/api/menus";
import * as orders from "./api/orders";
import * as paymentMethods from "./api/paymentMethods";
import * as posActionTypes from "../constants/posActionTypes";
import * as posModels from "./api/posModels";
import * as posReducers from "./posReducers";
import * as posSelectors from "./posSelectors";
import * as reduxModels from "./reduxModels";
import * as Sentry from "@sentry/capacitor";
import * as settings from "../lib/api/settings";
import * as stores from "../lib/api/stores";
import * as till from "../lib/api/till";
import describeNetworkError from "./describeNetworkError";
import logger from "./sentryLogger";
import reduxToPosItemCustomization from "./reduxToPosItemCustomization";
import {Actions, API, Constants, Lib, Reducers, ReduxModels} from "habit-core";
import {AnyAction} from "redux";
import {CurrentOrderState} from "habit-core/types/reducers/currentOrderReducer";
import {MenuItemModifier} from "habit-core/types/api/models";
import {openCashDrawer} from "./CashDrawer/CashDrawerPlugin";
import {setInstanceHeader} from "../lib/api/instance";
import {ThunkAction} from "redux-thunk";
import {v4 as uuid4} from "uuid";
import {AxiosError} from "axios";

type PosRootState = Reducers.RootState & {
    pos: posReducers.PosState;
};

type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    PosRootState,
    unknown,
    AnyAction
>;

const cartEntityRemovalErrorMessages = {
    hasCCPayments:
        "A partial credit card payment has been applied to this order. To clear this order, you must complete the purchase or cancel the order.",
    hasNonCCPayments:
        "A partial payment has been applied to this order. To clear this order, you must complete the purchase or remove those applied payments.",
};

export function setDeviceId(deviceId: string): AnyAction {
    return {
        type: posActionTypes.POS_SET_DEVICE_ID,
        deviceId,
    };
}

export function initializeStation(
    mode: posModels.StationMode,
    requiresDriveThruDetails: boolean,
    laneAssignment: string | null,
    autoPrintReceipts: boolean,
): AnyAction {
    return {
        type: posActionTypes.POS_STATION_INITIALIZE,
        mode,
        requiresDriveThruDetails,
        laneAssignment,
        autoPrintReceipts,
    };
}

export function editStationModeImmediate(
    stationMode: posModels.StationMode,
    laneAssignment: posModels.LaneAssignment | null,
): AnyAction {
    return {
        type: posActionTypes.POS_STATION_EDIT_MODE,
        stationMode,
        laneAssignment,
    };
}

export function editStationMode(
    stationMode: posModels.StationMode,
    laneAssignment: posModels.LaneAssignment | null,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        return settings
            .recordChangeStationMode(stationMode, laneAssignment)
            .then(() => {
                dispatch(editStationModeImmediate(stationMode, laneAssignment));
                setInstanceHeader("X-POS-Station-Mode", stationMode);
            });
    };
}

function editAutoPrintReceiptsImmediate(autoPrintReceipts: boolean): AnyAction {
    return {
        type: posActionTypes.POS_STATION_EDIT_AUTO_PRINT_RECEIPTS,
        autoPrintReceipts,
    };
}

export function editAutoPrintReceipts(
    autoPrintReceipts: boolean,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        return autoPrintReceiptsStorage
            .setStoredAutoPrintReceipts(autoPrintReceipts)
            .then(() => {
                dispatch(editAutoPrintReceiptsImmediate(autoPrintReceipts));
            });
    };
}

export function setStationCashier(cashier: reduxModels.Cashier): AnyAction {
    return {
        type: posActionTypes.POS_STATION_SET_CASHIER,
        cashier,
    };
}

export function setStationSubmittingOrder(
    isSubmittingOrder: boolean,
): AnyAction {
    return {
        type: posActionTypes.POS_STATION_SET_IS_SUBMITTING_ORDER,
        isSubmittingOrder,
    };
}

export function setStationCCPaymentDisabled(
    isCCPaymentDisabled: boolean,
): AnyAction {
    return {
        type: posActionTypes.POS_STATION_SET_IS_CC_PAYMENT_DISABLED,
        isCCPaymentDisabled,
    };
}

function toggleStationTrainingMode(turnOn: boolean): AnyAction {
    return {
        type: posActionTypes.POS_STATION_TOGGLE_TRAINING_MODE,
        isTraining: turnOn,
    };
}

export function startTrainingMode(): AppThunk {
    return (dispatch) => {
        dispatch(toggleStationTrainingMode(true));
        dispatch(setTrainingModeAssignedTillImmediate(-1));
    };
}

export function endTrainingMode(): AppThunk {
    return (dispatch) => {
        dispatch(toggleStationTrainingMode(false));
    };
}

// copied over from core
function setStoreMenuId(
    storeId: string,
    menuId: string,
    orderType: API.models.OrderType,
): AnyAction {
    return {
        type: Constants.actionTypes.CURRENT_ORDER_SET_STORE_MENU,
        storeId,
        orderType: orderType,
        menuId,
    };
}

export function refreshStoreMenu(
    storeId: string,
    orderType: API.models.OrderType,
): AppThunk {
    return async (dispatch) => {
        const {menus} = await API.menus.getByStore(storeId);
        // order type should only be "dine_in", "carry_out" or "drive_thru"
        let menuType: API.models.MenuType = "lunch_and_dinner";
        if (orderType === "drive_thru") {
            menuType = "lunch_and_dinner_drive_thru";
        }
        const activeMenu = menus?.find((m) => m.type === menuType);
        const menuId = activeMenu?.id;

        if (!menuId) {
            throw new Error(
                `Store ${storeId} does not have a menu for orderType ${orderType}`,
            );
        }

        dispatch(Actions.menuActions.clearAndSetMenus(menus, storeId));
        dispatch(setStoreMenuId(storeId, menuId, orderType));
    };
}

function initializeAllPrepsImmediate(
    preps: posModels.AllPrepsItemModifierData[],
) {
    return {
        type: posActionTypes.POS_ALL_PREPS_INITIALIZE,
        preps,
    };
}

export function initializePosAllPreps(
    storeId: string,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        return menuMethods.getAllPreps(storeId).then((allPrepResponse) => {
            const allPreps = allPrepResponse.preps;
            const formattedAllPreps = allPreps.map((prep) => {
                return {
                    id: prep.id,
                    name: prep.name,
                    digitalOnly: prep.digitalOnly,
                    isAllergy: prep.isAllergy,
                    selections: prep.selections.map((selection) => {
                        return {
                            id: selection.id,
                            default:
                                prep.defaultModifierSelectionId ===
                                selection.id,
                            name: selection.name,
                            priceCents: selection.priceCents,
                            type: selection.type,
                        };
                    }),
                    isolatedImages128: [],
                };
            });
            dispatch(initializeAllPrepsImmediate(formattedAllPreps));
            return;
        });
    };
}

function initializeAddOnsImmediate(
    itemsAddOnsInfo: posModels.ItemAddOnsInfo[],
) {
    return {
        type: posActionTypes.POS_ADD_ONS_INITIALIZE,
        itemsAddOnsInfo,
    };
}

export function initializePosAddOns(): AppThunk<Promise<void>> {
    return (dispatch) => {
        return menuMethods.getItemsAddOnsInfo().then((itemsAddOnsResponse) => {
            const itemsAddOnsInfo = itemsAddOnsResponse.items;
            dispatch(initializeAddOnsImmediate(itemsAddOnsInfo));
            return;
        });
    };
}

function setCurrentOrderId(id: string): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_ID,
        id,
    };
}

function setCurrentOrderReferenceNumber(referenceNumber: number): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_REF_NUM,
        referenceNumber,
    };
}

function setCurrentOrderIdRefNumAndNumber(
    id: string,
    referenceNumber: number,
    orderNumber: number,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_ID_REF_NUM_AND_NUMBER,
        id,
        referenceNumber,
        orderNumber,
    };
}

function setCurrentOrderOrderNumber(orderNumber: number): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_ORDER_NUMBER,
        orderNumber,
    };
}

function removeCurrentOrderRefNumImmediate(): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_REMOVE_REF_NUM,
    };
}

function setReserveOrderIdErrored(reserveOrderIdErrored: boolean): AnyAction {
    return {
        type: posActionTypes.POS_SET_RESERVE_ORDER_ID_ERRORED,
        reserveOrderIdErrored,
    };
}

export function reserveCurrentOrderId(): AppThunk<Promise<void>> {
    return (dispatch, getState) => {
        const state = getState();
        const deviceId = state.pos.deviceId;
        const isTraining = state.pos.station.isTraining;
        dispatch(setReserveOrderIdErrored(false));
        return orders
            .reserveOrderId(deviceId ?? "", isTraining)
            .then((data) => {
                dispatch(setCurrentOrderId(data.id));
            })
            .catch((err) => {
                dispatch(setReserveOrderIdErrored(true));
                const errMsg = describeNetworkError(err).join("\n");
                logger.warn("Failed to reserve new order id.", {
                    errorMessage: errMsg,
                });
            });
    };
}

export function reserveCurrentOrderRefNumIfNeeded(): AppThunk<Promise<void>> {
    return (dispatch, getState) => {
        const state = getState();
        const posCurrentOrder = state.pos.currentOrder;
        const isTraining = state.pos.station.isTraining;
        if (posCurrentOrder.referenceNumber !== null) {
            return Promise.resolve();
        }

        return orders.reserveOrderReferenceNumber(isTraining).then((data) => {
            dispatch(setCurrentOrderReferenceNumber(data.referenceNumber));
        });
    };
}

export function removeCurrentOrderRefNum(): AppThunk {
    return (dispatch) => {
        dispatch(removeCurrentOrderRefNumImmediate());
    };
}

function clearCurrentOrderImmediate(): AnyAction {
    return {type: posActionTypes.POS_CURRENT_ORDER_CLEAR};
}

function clearCurrentOrderVoidedImmediate(): AnyAction {
    return {type: posActionTypes.POS_CURRENT_ORDER_VOIDED_CLEAR};
}

export function clearCurrentOrder(): AppThunk {
    return (dispatch, getState) => {
        const state = getState();
        const storeId = state.pos.store.id;
        if (!storeId) {
            throw new Error(errors.NO_STORE_ID);
        }
        dispatch(
            Actions.currentOrderActions.clearCurrentOrderProductsImmediate(),
        );
        dispatch(Actions.currentOrderActions.setShouldRoundUp(false));
        dispatch(clearCurrentOrderImmediate());
        dispatch(clearCurrentOrderVoidedImmediate());
        dispatch(setPlaceOrderAfterPaymentProcessedErrored(false));
        dispatch(setCurrentOrderPlacementErrorMessage(undefined));
    };
}

function setOrders(orders: posModels.OrderParsed[]): AnyAction {
    return {
        type: posActionTypes.POS_ORDERS_SET,
        orders,
    };
}

function addOrder(order: posModels.OrderParsed): AnyAction {
    return {
        type: posActionTypes.POS_ADD_ORDER,
        order,
    };
}

export function getOrders(
    statuses: posModels.OrderStatus[],
    sources: posModels.OrderSource[],
    training = false,
): AppThunk<Promise<posModels.OrderParsed[]>> {
    return (dispatch) => {
        return orders.get(statuses, sources, training).then((data) => {
            if (statuses.includes("pending")) {
                const nonPendingOrders: posModels.OrderParsed[] = [];
                const pendingOrders: posModels.OrderParsed[] = [];
                data.orders.forEach((order) => {
                    if (order.status === "pending") {
                        pendingOrders.push(order);
                    } else {
                        nonPendingOrders.push(order);
                    }
                });
                dispatch(setOrders(nonPendingOrders));
                dispatch(setPendingOrders(pendingOrders));
            } else {
                dispatch(setOrders(data.orders));
            }
            return data.orders;
        });
    };
}

function setPendingOrders(orders: posModels.OrderParsed[]): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_ORDERS_SET,
        orders,
    };
}

export function getDriveThruPendingOrders(
    training = false,
): AppThunk<Promise<posModels.OrderParsed[]>> {
    return (dispatch) => {
        return orders.getDriveThruPendingOrders(training).then((data) => {
            dispatch(setPendingOrders(data.orders));
            return data.orders;
        });
    };
}

export function getKioskPendingOrders(
    training = false,
): AppThunk<Promise<posModels.OrderParsed[]>> {
    return (dispatch) => {
        return orders.getKioskPendingOrders(training).then((data) => {
            dispatch(setPendingOrders(data.orders));
            return data.orders;
        });
    };
}

function maskCardNumber(cardNumber: string): string {
    return `${"*".repeat(cardNumber.length - 4)}${cardNumber.slice(-4)}`;
}

function maskGiftCardNumber(cardNumber: string): string {
    return `${"*".repeat(cardNumber.length - 6)}${cardNumber.slice(-6)}`;
}

export function setCurrentOrderPrintReceipt(printReceipt: boolean): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_PRINT_RECEIPT,
        printReceipt,
    };
}

function setCurrentOrderIsSubmitted(isSubmitted: boolean): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_IS_SUBMITTED,
        isSubmitted,
    };
}

export function setCurrentOrderPlacementErrorMessage(
    currentOrderPlacmentErrorMessage: string | undefined,
): AnyAction {
    return {
        type: posActionTypes.POS_ERRORS_SET_CURRENT_ORDER_PLACEMENT_ERROR_MESSAGE,
        currentOrderPlacmentErrorMessage,
    };
}

const orderPlacementErrorStrings = {
    errorAfterPaymentsProcessedMessage: (orderId: string) =>
        `Payment was successfully captured, but this order failed to process and was not sent to the kitchen. Please note the order contents and send them manually to the kitchen.\n\nOrder Id: ${orderId}`,
};

export function processPaymentAndPlaceCurrentOrder(): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const isParallel = state.pos.app.isParallel;
        const isTraining = state.pos.station.isTraining;
        const tillAssignmentSkipped = state.pos.station.tillAssignmentSkipped;

        const posCurrentOrder = state.pos.currentOrder;

        /* payments */
        const cashPaidCents = state.pos.currentOrder.payment.cashPaidCents;

        const penniesUnavailable = state.pos.store.penniesUnavailable;

        const giftCardsApplied =
            state.pos.currentOrder.payment.giftCardsApplied;

        const compCardsApplied =
            state.pos.currentOrder.payment.compCardsApplied;

        const creditCardsApplied =
            state.pos.currentOrder.payment.creditCardsApplied;

        const externalTendersApplied =
            state.pos.currentOrder.payment.externalTendersApplied;

        const ezCaterApplied = state.pos.currentOrder.payment.ezCaterApplied;

        const staffTipCents: API.models.USDCents = creditCardsApplied.reduce(
            (sum, creditCard) => creditCard.tipAmountCents + sum,
            0,
        );

        /* order summary */
        const subtotalCents =
            posSelectors.getCurrentOrderSubtotalSansVoidedCents(state);

        const appliedCharityAmountCents =
            posSelectors.getCurrentOrderAppliedCharityAmountCents(
                state,
                compCardsApplied,
                externalTendersApplied,
            );
        const taxCents = posSelectors.getCurrentOrderTaxSansVoidedCents(
            state,
            compCardsApplied,
            externalTendersApplied,
        );
        const orderTotalCents =
            subtotalCents + appliedCharityAmountCents + taxCents;
        const discountAmountCents = Math.min(
            posSelectors.getCurrentOrderDiscountAmountCents(state),
            subtotalCents,
        );
        const orderTotalInclusiveOfTip = orderTotalCents + (staffTipCents ?? 0);

        const paidCents = posSelectors.getCurrentOrderPaidCents(state);

        dispatch(setStationSubmittingOrder(true));

        try {
            let tempChangeOwedCents =
                paidCents > orderTotalInclusiveOfTip
                    ? paidCents - orderTotalInclusiveOfTip
                    : 0;
            // Round up to nearest 5 cents if pennies unavailable
            let changeOwedRoundingAdjustmentCents: number | null = null;
            if (penniesUnavailable && tempChangeOwedCents % 5 > 0) {
                changeOwedRoundingAdjustmentCents =
                    5 - (tempChangeOwedCents % 5);
                tempChangeOwedCents =
                    tempChangeOwedCents + changeOwedRoundingAdjustmentCents;
            }
            const cashDetails: posModels.PaymentDetail[] =
                cashPaidCents !== 0
                    ? [
                          {
                              type: "cash",
                              amountCents: cashPaidCents,
                              changeOwedCents: tempChangeOwedCents,
                              changeOwedRoundingAdjustmentCents,
                          },
                      ]
                    : [];

            const creditCardDetails: posModels.PaymentDetail[] =
                creditCardsApplied.map((creditCardApplied) => {
                    return {
                        type: "credit",
                        maskedCardNumber: maskCardNumber(
                            creditCardApplied.cardNumber,
                        ),
                        authId: creditCardApplied.authId,
                        termId: creditCardApplied.termId,
                        merchId: creditCardApplied.merchId,
                        amountCents: creditCardApplied.appliedAmountCents,
                        ccType: creditCardApplied.ccType,
                        cardExpMonth: creditCardApplied.cardExpMonth,
                        cardExpYear: creditCardApplied.cardExpYear,
                        cardToken: creditCardApplied.cardToken,
                        ctroutd: creditCardApplied.ctroutd,
                        troutd: creditCardApplied.troutd,
                        tipCents: creditCardApplied.tipAmountCents,
                    };
                });

            const giftCardDetails: posModels.PaymentDetail[] = [];
            for (const gc of giftCardsApplied) {
                let balanceCents = 0;
                let authId = "";
                let cardToken = "";
                if (isParallel) {
                    balanceCents = gc.balanceRemainingCents;
                } else {
                    const response = await paymentMethods.reduceGiftCardBalance(
                        posCurrentOrder.id ?? "",
                        gc.id,
                        gc.appliedCents,
                    );
                    balanceCents = response.balanceCents;
                    authId = response.authId;
                    cardToken = response.cardToken;
                }

                giftCardDetails.push({
                    type: "gift_card",
                    maskedCardNumber: maskGiftCardNumber(gc.id),
                    amountCents: gc.appliedCents,
                    balanceCents,
                    cardToken,
                    authId,
                });
            }

            const compCardsDetails: posModels.PaymentDetail[] = [];
            for (const cc of compCardsApplied) {
                let balanceCents = 0;
                let authId = "";
                let cardToken = "";
                if (isParallel) {
                    balanceCents = cc.balance - (cc.appliedCents ?? 0);
                } else {
                    const response = await paymentMethods.reduceGiftCardBalance(
                        posCurrentOrder.id ?? "",
                        cc.id,
                        cc.appliedCents ?? 0,
                    );
                    balanceCents = response.balanceCents;
                    authId = response.authId;
                    cardToken = response.cardToken;
                }

                giftCardDetails.push({
                    type: "comp_card",
                    maskedCardNumber: maskGiftCardNumber(cc.id),
                    amountCents: cc.appliedCents ?? 0,
                    balanceCents,
                    authId,
                    cardToken,
                });
            }

            const externalTenderDetails: posModels.PaymentDetail[] =
                externalTendersApplied.map((tender) => {
                    return {
                        type: "external_tender",
                        tenderName: tender.tenderName,
                        amountCents: tender.appliedCents ?? 0,
                    };
                });

            const paymentDetails: posModels.PaymentDetail[] = [
                ...cashDetails,
                ...giftCardDetails,
                ...compCardsDetails,
                ...creditCardDetails,
                ...externalTenderDetails,
            ];

            if (ezCaterApplied) {
                paymentDetails.push({
                    type: "ez_cater",
                    amountCents: orderTotalCents - discountAmountCents,
                });
            }

            try {
                await dispatch(reserveCurrentOrderRefNumIfNeeded());
            } catch (err) {
                logger.error(
                    "Failed to reserve order reference number after processing payments.",
                    {
                        orderId: posCurrentOrder.id,
                        errorMessage: describeNetworkError(
                            err as AxiosError,
                        ).join("\n"),
                    },
                );
                dispatch(
                    setCurrentOrderPlacementErrorMessage(
                        orderPlacementErrorStrings.errorAfterPaymentsProcessedMessage(
                            posCurrentOrder.id ?? "",
                        ),
                    ),
                );
                dispatch(setPlaceOrderAfterPaymentProcessedErrored(true));
                dispatch(setStationSubmittingOrder(false));
                return;
            }

            try {
                if (cashPaidCents !== 0 && !tillAssignmentSkipped) {
                    await openCashDrawer(isTraining);
                }
                // eslint-disable-next-line no-empty
            } catch {} // error is already logged with sentry and we want to allow the purchase to go through.

            try {
                await dispatch(placeCurrentOrder("paid", paymentDetails));
            } catch (err) {
                logger.error(
                    "Failed to place order after processing payments.",
                    {
                        orderId: posCurrentOrder.id,
                        errorMessage: describeNetworkError(
                            err as AxiosError,
                        ).join("\n"),
                    },
                );
                dispatch(
                    setCurrentOrderPlacementErrorMessage(
                        orderPlacementErrorStrings.errorAfterPaymentsProcessedMessage(
                            posCurrentOrder.id ?? "",
                        ),
                    ),
                );
                dispatch(setPlaceOrderAfterPaymentProcessedErrored(true));
                dispatch(setStationSubmittingOrder(false));
                return;
            }

            if (tempChangeOwedCents) {
                dispatch(setPaymentChangeOwedCents(tempChangeOwedCents));
            }
        } catch (err) {
            const description = describeNetworkError(err as AxiosError).join(
                "\n",
            );
            logger.error(
                "An error occurred while processing payments for the current order. Failed to place order.",
                {
                    orderId: posCurrentOrder.id,
                    errorMessage: description,
                },
            );
            dispatch(setCurrentOrderPlacementErrorMessage(description));
            dispatch(setStationSubmittingOrder(false));
        }
    };
}

export function placeCurrentOrder(
    orderStatus: posModels.OrderStatus,
    payments: posModels.PaymentDetail[] | null,
): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const guestName = state.pos.currentOrder.guestName;
        const pagerNumber = state.pos.currentOrder.pagerNumber;
        const menuId = state.currentOrder.menuId ?? "";
        const stationMode = state.pos.station.mode;
        const deviceId = state.pos.deviceId;
        const orderId = state.pos.currentOrder.id;
        if (!orderId) {
            return Promise.reject(new Error(errors.ERR_NO_ORDER_ID));
        }
        const referenceNumber = state.pos.currentOrder.referenceNumber;
        if (referenceNumber === null && orderStatus !== "in_creation") {
            return Promise.reject(new Error(errors.ERR_NO_REFERENCE_NUMBER));
        }

        const combos: posModels.OrderComboRequest[] =
            state.currentOrder.comboCustomizationIds.map((id) => {
                const cc = state.customizations.combos.byInternalId[id];
                return {
                    comboId: cc.comboId,
                    quantity: cc.quantity,
                    items: cc.itemCustomizationIds.map((icid) =>
                        reduxToPosItemCustomization(
                            state.customizations.items.byInternalId[icid],
                        ),
                    ),
                    dateAdded: cc.dateAdded,
                    dateModified: cc.dateModified,
                };
            });
        const voidCombos: posModels.OrderComboRequest[] =
            state.pos.currentOrder.voidedComboIds.map((id) => {
                const vc = state.pos.currentOrderVoided.combos.voided.byId[id];
                const cc =
                    state.customizations.combos.byInternalId[
                        vc.comboCustomizationId
                    ];
                return {
                    comboId: cc.comboId,
                    quantity: vc.quantity * -1,
                    items: cc.itemCustomizationIds.map((icid) =>
                        reduxToPosItemCustomization(
                            state.customizations.items.byInternalId[icid],
                        ),
                    ),
                    dateAdded: cc.dateAdded,
                    dateModified: cc.dateModified,
                };
            });

        const items: posModels.OrderItemRequest[] =
            state.currentOrder.itemCustomizationIds.map((icid) => {
                const ic = state.customizations.items.byInternalId[icid];
                return {
                    quantity: ic.quantity,
                    customization: reduxToPosItemCustomization(ic),
                    dateAdded: ic.dateAdded,
                    dateModified: ic.dateModified,
                };
            });
        const voidItems: posModels.OrderItemRequest[] =
            state.pos.currentOrder.voidedItemIds.map((id) => {
                const vi = state.pos.currentOrderVoided.items.voided.byId[id];
                const ic =
                    state.customizations.items.byInternalId[
                        vi.itemCustomizationId
                    ];
                return {
                    quantity: vi.quantity * -1,
                    customization: reduxToPosItemCustomization(ic),
                    dateAdded: ic.dateAdded,
                    dateModified: ic.dateModified,
                };
            });

        const giftCards: posModels.OrderGiftCardRequest[] = [];
        // in v1, can't buy gift cards on a normal order
        if (
            items.length === 0 &&
            voidItems.length === 0 &&
            combos.length === 0 &&
            voidCombos.length === 0
        ) {
            const giftCardsToPurchase =
                state.pos.currentOrder.giftCards.purchase;
            const giftCardsToAddFunds =
                state.pos.currentOrder.giftCards.addFunds;
            if (giftCardsToPurchase.length !== 0) {
                for (const gcPurchaseInfo of giftCardsToPurchase) {
                    for (let i = 0; i < gcPurchaseInfo.quantity; i++) {
                        giftCards.push({
                            cardNumber: gcPurchaseInfo.cardNumbers?.[i] ?? "",
                            amountCents: gcPurchaseInfo.balanceCents,
                        });
                    }
                }
            }
            if (giftCardsToAddFunds.length != 0) {
                for (const gcAddFundsInfo of giftCardsToAddFunds) {
                    giftCards.push({
                        cardNumber: gcAddFundsInfo.cardNumber,
                        amountCents: gcAddFundsInfo.amountToAddCents,
                    });
                }
            }
        }

        let orderType: posModels.OrderType = "in_store";
        if (state.currentOrder.orderType === "drive_thru") {
            orderType = "drive_thru";
        } else if (state.currentOrder.orderType === "carry_out") {
            orderType = "carry_out";
        }
        if (state.pos.currentOrder.payment.ezCaterApplied) {
            orderType =
                state.pos.currentOrder.ezCaterOrderType === "delivery"
                    ? "ez_cater_delivery"
                    : "ez_cater_pickup";
        }

        const subtotalCents =
            posSelectors.getCurrentOrderSubtotalSansVoidedCents(state);
        const taxCents = posSelectors.getCurrentOrderTaxSansVoidedCents(
            state,
            state.pos.currentOrder.payment.compCardsApplied,
            state.pos.currentOrder.payment.externalTendersApplied,
        );

        let account: posModels.DiscountAccount | null = null; // Check if any discount associated with digital customer account
        let redemptionDetails: posModels.RedemptionDetail[] | null = null;
        if (state.pos.currentOrder.discounts.length) {
            let subtotalCentsWithDiscount = subtotalCents;
            redemptionDetails = [];
            state.pos.currentOrder.discounts.forEach((discount) => {
                let amountCents = 0;
                if (subtotalCentsWithDiscount > 0) {
                    if (discount.amountCents) {
                        amountCents = Math.min(
                            subtotalCentsWithDiscount,
                            discount.amountCents,
                        );
                        subtotalCentsWithDiscount -= discount.amountCents;
                    } else if (discount.amountPercentage) {
                        const percentageAmount = Lib.currency.roundFloat(
                            (subtotalCents *
                                (state.pos.currentOrder.discounts[0]
                                    .amountPercentage ?? 0)) /
                                100.0,
                        );
                        amountCents = Math.min(
                            subtotalCentsWithDiscount,
                            percentageAmount,
                        );
                        subtotalCentsWithDiscount -= amountCents;
                    }
                }
                redemptionDetails?.push({
                    code: discount.discountCode,
                    id: discount.rewardId,
                    amountCents: amountCents,
                    type: discount.discountType,
                    managerId: discount.managerId ?? null,
                    employeeId: discount.employeeId ?? null,
                    details: discount.details ?? null,
                });

                if (discount.account && !account) {
                    account = discount.account;
                }
            });
        }

        let startTime = state.pos.currentOrder.startTime;
        if (!startTime) {
            logger.warn("Placing current order without valid start time", {
                orderId: orderId,
            });
            startTime = new Date();
        }

        const charityRoundUpSelected = state.currentOrder.shouldRoundUp;
        const charityAmountCents =
            posSelectors.getCurrentOrderAppliedCharityAmountCents(
                state,
                state.pos.currentOrder.payment.compCardsApplied,
                state.pos.currentOrder.payment.externalTendersApplied,
            );
        const creditCardsApplied =
            state.pos.currentOrder.payment.creditCardsApplied;
        const staffTipCents =
            creditCardsApplied.length > 0
                ? creditCardsApplied.reduce(
                      (sum, creditCard) => creditCard.tipAmountCents + sum,
                      0,
                  )
                : null;
        const totalCents =
            subtotalCents +
            taxCents +
            charityAmountCents +
            (staffTipCents ?? 0);
        const driveThruDetails =
            orderType === "drive_thru"
                ? state.pos.currentOrder.driveThruDetails
                : null;
        const fundraiserCode = state.currentOrder.fundraiserCode;
        const qsrSelections = state.pos.currentOrder.qsrSelections;
        const skipReceiptPrinting = !state.pos.currentOrder.printReceipt;

        // The entire order is tax exempt if it has been manually marked as tax exempt,
        // or all payments have been made with tax exempt external tenders
        const paymentInfo = state.pos.currentOrder.payment;
        const paidWithTaxExemptExternalTenders =
            paymentInfo.cashPaidCents === 0 &&
            !paymentInfo.ezCaterApplied &&
            paymentInfo.creditCardsApplied.length === 0 &&
            paymentInfo.giftCardsApplied.length === 0 &&
            paymentInfo.compCardsApplied.length === 0 &&
            paymentInfo.externalTendersApplied.every(
                (tender) => tender.isTaxExempt,
            );
        const entireOrderTaxExempt =
            state.pos.currentOrder.isTaxExempt ||
            paidWithTaxExemptExternalTenders;

        if (!state.pos.station.isSubmittingOrder) {
            dispatch(setStationSubmittingOrder(true));
        }

        return orders
            .place(
                orderId,
                referenceNumber,
                deviceId ?? "",
                stationMode,
                menuId,
                null,
                guestName,
                pagerNumber,
                [...combos, ...voidCombos],
                [...items, ...voidItems],
                giftCards,
                orderType,
                charityAmountCents,
                charityRoundUpSelected,
                staffTipCents,
                subtotalCents,
                taxCents,
                totalCents,
                orderStatus,
                driveThruDetails,
                redemptionDetails,
                startTime,
                payments,
                fundraiserCode,
                qsrSelections,
                skipReceiptPrinting,
                entireOrderTaxExempt,
                account,
            )
            .then((order) => {
                if (totalCents === 0) {
                    logs.logGeneralAction("$0-order-placed", stationMode);
                }

                if (order.status !== "in_creation") {
                    dispatch(addOrder(order));
                    dispatch(setCurrentOrderEndTime(new Date()));
                    dispatch(setCurrentOrderOrderNumber(order.orderNumber));
                    dispatch(setCurrentOrderIsSubmitted(true));
                }

                if (order.status === "paid") {
                    dispatch(removePendingOrder(order.id));
                }
            })
            .finally(() => {
                dispatch(setStationSubmittingOrder(false));
            });
    };
}

export function updateDriveThruOrderContents(): AppThunk<Promise<void>> {
    return (dispatch, getState) => {
        const state = getState();

        const deviceId = state.pos.deviceId;

        const orderId = state.pos.currentOrder.id;
        if (!orderId) {
            return Promise.reject(new Error(errors.ERR_NO_ORDER_ID));
        }
        const menuId = state.currentOrder.menuId ?? "";
        const qsrSelections = state.pos.currentOrder.qsrSelections;
        const driveThruDetails = state.pos.currentOrder.driveThruDetails;

        const combos: posModels.OrderComboRequest[] =
            state.currentOrder.comboCustomizationIds.map((id) => {
                const cc = state.customizations.combos.byInternalId[id];
                return {
                    comboId: cc.comboId,
                    quantity: cc.quantity,
                    items: cc.itemCustomizationIds.map((icid) =>
                        reduxToPosItemCustomization(
                            state.customizations.items.byInternalId[icid],
                        ),
                    ),
                    dateAdded: cc.dateAdded,
                    dateModified: cc.dateModified,
                };
            });
        const voidCombos: posModels.OrderComboRequest[] =
            state.pos.currentOrder.voidedComboIds.map((id) => {
                const vc = state.pos.currentOrderVoided.combos.voided.byId[id];
                const cc =
                    state.customizations.combos.byInternalId[
                        vc.comboCustomizationId
                    ];
                return {
                    comboId: cc.comboId,
                    quantity: vc.quantity * -1,
                    items: cc.itemCustomizationIds.map((icid) =>
                        reduxToPosItemCustomization(
                            state.customizations.items.byInternalId[icid],
                        ),
                    ),
                    dateAdded: cc.dateAdded,
                    dateModified: cc.dateModified,
                };
            });

        const items: posModels.OrderItemRequest[] =
            state.currentOrder.itemCustomizationIds.map((icid) => {
                const ic = state.customizations.items.byInternalId[icid];
                return {
                    quantity: ic.quantity,
                    customization: reduxToPosItemCustomization(ic),
                    dateAdded: ic.dateAdded,
                    dateModified: ic.dateModified,
                };
            });
        const voidItems: posModels.OrderItemRequest[] =
            state.pos.currentOrder.voidedItemIds.map((id) => {
                const vi = state.pos.currentOrderVoided.items.voided.byId[id];
                const ic =
                    state.customizations.items.byInternalId[
                        vi.itemCustomizationId
                    ];
                return {
                    quantity: vi.quantity * -1,
                    customization: reduxToPosItemCustomization(ic),
                    dateAdded: ic.dateAdded,
                    dateModified: ic.dateModified,
                };
            });

        const giftCards: posModels.OrderGiftCardRequest[] = [];
        // in v1, can't buy gift cards on a normal order
        if (
            items.length === 0 &&
            voidItems.length === 0 &&
            combos.length === 0 &&
            voidCombos.length === 0
        ) {
            const giftCardsToPurchase =
                state.pos.currentOrder.giftCards.purchase;
            const giftCardsToAddFunds =
                state.pos.currentOrder.giftCards.addFunds;
            if (giftCardsToPurchase.length !== 0) {
                for (const gcPurchaseInfo of giftCardsToPurchase) {
                    for (let i = 0; i < gcPurchaseInfo.quantity; i++) {
                        giftCards.push({
                            cardNumber: gcPurchaseInfo.cardNumbers?.[i] ?? "",
                            amountCents: gcPurchaseInfo.balanceCents,
                        });
                    }
                }
            }
            if (giftCardsToAddFunds.length != 0) {
                for (const gcAddFundsInfo of giftCardsToAddFunds) {
                    giftCards.push({
                        cardNumber: gcAddFundsInfo.cardNumber,
                        amountCents: gcAddFundsInfo.amountToAddCents,
                    });
                }
            }
        }

        const charityRoundUpSelected = state.currentOrder.shouldRoundUp;
        const charityRoundUpAmountCents =
            posSelectors.getCharityRoundUpSansVoidedCents(state, [], []);
        const charityAmountCents = state.pos.currentOrder.charityAmountCents;
        const appliedCharityAmountCents = !charityAmountCents
            ? charityRoundUpAmountCents
            : charityAmountCents;

        const subtotalCents =
            posSelectors.getCurrentOrderSubtotalSansVoidedCents(state);
        const taxCents = posSelectors.getCurrentOrderTaxSansVoidedCents(
            state,
            [],
            [],
        );
        const totalCents = subtotalCents + taxCents;

        let startTime = state.pos.currentOrder.startTime;
        if (!startTime) {
            logger.warn("Updating drive thru order without valid start time", {
                orderId: orderId,
            });
            startTime = new Date();
        }

        return orders
            .place(
                orderId,
                null,
                deviceId ?? "",
                "drive_thru_order_taker",
                menuId,
                null,
                null,
                null,
                [...combos, ...voidCombos],
                [...items, ...voidItems],
                giftCards,
                "drive_thru",
                appliedCharityAmountCents,
                charityRoundUpSelected,
                null,
                subtotalCents,
                taxCents,
                totalCents,
                "in_creation",
                driveThruDetails,
                null,
                startTime,
                null,
                null,
                qsrSelections,
                true,
                false,
                null,
            )
            .then(() => {
                return;
            })
            .catch((err) => {
                const errMsg = describeNetworkError(err).join("\n");
                logger.warn("Failed to update drive thru order.", {
                    orderId: orderId,
                    errMsg: errMsg,
                });
            });
    };
}

export function removePendingOrder(orderId: string): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_ORDERS_REMOVE,
        orderId,
    };
}

// TODO: refresh the pending orders based on what type of order is being cancelled (drive thru or kiosk).
// function now returns the new set of pending orders (also updates state)
export function cancelOrder(
    orderId: string,
    managerId?: string,
): AppThunk<Promise<posModels.OrderParsed[]>> {
    return (dispatch, getState) => {
        const state = getState();
        const stationMode = state.pos.station.mode;

        return orders
            .cancel(orderId, state.pos.currentOrder.isTaxExempt)
            .then((order) => {
                if (managerId) {
                    logs.logManagerAction(
                        "cancel-order",
                        managerId,
                        stationMode,
                    );
                }

                dispatch(addOrder(order));
                dispatch(removePendingOrder(orderId));
                const getPendingFunc =
                    order.source === "kiosk"
                        ? getKioskPendingOrders
                        : getDriveThruPendingOrders;
                return dispatch(getPendingFunc());
            });
    };
}

// makes call to cancel order if cc payment was applied, just resolves otherwise.
export function cancelCurrentOrder(
    managerId?: string,
): AppThunk<Promise<void>> {
    return (dispatch, getState) => {
        const state = getState();
        const storeId = state.pos.store.id;
        const posCurrentOrder = state.pos.currentOrder;
        const stationMode = state.pos.station.mode;

        if (!storeId) {
            throw new Error(errors.NO_STORE_ID);
        }

        if (posCurrentOrder.id === null) {
            throw new Error(errors.ERR_NO_ORDER_ID);
        }

        const creditCardsApplied = posCurrentOrder.payment.creditCardsApplied;
        if (creditCardsApplied.length > 0) {
            const menuId = state.currentOrder.menuId ?? "";

            const creditCardDetails: posModels.PaymentDetail[] =
                creditCardsApplied.map((creditCardApplied) => {
                    return {
                        type: "credit",
                        maskedCardNumber: maskCardNumber(
                            creditCardApplied.cardNumber,
                        ),
                        authId: creditCardApplied.authId,
                        termId: creditCardApplied.termId,
                        merchId: creditCardApplied.merchId,
                        amountCents: creditCardApplied.appliedAmountCents,
                        ccType: creditCardApplied.ccType,
                        cardExpMonth: creditCardApplied.cardExpMonth,
                        cardExpYear: creditCardApplied.cardExpYear,
                        cardToken: creditCardApplied.cardToken,
                        ctroutd: creditCardApplied.ctroutd,
                        troutd: creditCardApplied.troutd,
                        tipCents: creditCardApplied.tipAmountCents,
                    };
                });

            const combos: posModels.OrderComboRequest[] =
                state.currentOrder.comboCustomizationIds.map((id) => {
                    const cc = state.customizations.combos.byInternalId[id];
                    return {
                        comboId: cc.comboId,
                        quantity: cc.quantity,
                        items: cc.itemCustomizationIds.map((icid) =>
                            reduxToPosItemCustomization(
                                state.customizations.items.byInternalId[icid],
                            ),
                        ),
                        dateAdded: cc.dateAdded,
                        dateModified: cc.dateModified,
                    };
                });
            const voidCombos: posModels.OrderComboRequest[] =
                state.pos.currentOrder.voidedComboIds.map((id) => {
                    const vc =
                        state.pos.currentOrderVoided.combos.voided.byId[id];
                    const cc =
                        state.customizations.combos.byInternalId[
                            vc.comboCustomizationId
                        ];
                    return {
                        comboId: cc.comboId,
                        quantity: vc.quantity * -1,
                        items: cc.itemCustomizationIds.map((icid) =>
                            reduxToPosItemCustomization(
                                state.customizations.items.byInternalId[icid],
                            ),
                        ),
                        dateAdded: cc.dateAdded,
                        dateModified: cc.dateModified,
                    };
                });

            const items: posModels.OrderItemRequest[] =
                state.currentOrder.itemCustomizationIds.map((icid) => {
                    const ic = state.customizations.items.byInternalId[icid];
                    return {
                        quantity: ic.quantity,
                        customization: reduxToPosItemCustomization(ic),
                        dateAdded: ic.dateAdded,
                        dateModified: ic.dateModified,
                    };
                });
            const voidItems: posModels.OrderItemRequest[] =
                state.pos.currentOrder.voidedItemIds.map((id) => {
                    const vi =
                        state.pos.currentOrderVoided.items.voided.byId[id];
                    const ic =
                        state.customizations.items.byInternalId[
                            vi.itemCustomizationId
                        ];
                    return {
                        quantity: vi.quantity * -1,
                        customization: reduxToPosItemCustomization(ic),
                        dateAdded: ic.dateAdded,
                        dateModified: ic.dateModified,
                    };
                });

            const subtotalCents =
                posSelectors.getCurrentOrderSubtotalSansVoidedCents(state);
            const taxCents = posSelectors.getCurrentOrderTaxSansVoidedCents(
                state,
                [],
                [],
            );
            const totalCents = subtotalCents + taxCents;

            let startTime = state.pos.currentOrder.startTime;
            if (!startTime) {
                logger.warn(
                    "Updating drive thru order without valid start time",
                    {
                        orderId: state.pos.currentOrder.id,
                    },
                );
                startTime = new Date();
            }

            let orderType: posModels.OrderType = "in_store";
            if (state.currentOrder.orderType === "drive_thru") {
                orderType = "drive_thru";
            } else if (state.currentOrder.orderType === "carry_out") {
                orderType = "carry_out";
            }

            const referenceNumber =
                state.pos.currentOrder.referenceNumber ?? undefined;

            return orders
                .cancel(
                    posCurrentOrder.id,
                    state.pos.currentOrder.isTaxExempt,
                    referenceNumber,
                    creditCardDetails,
                    menuId,
                    [...combos, ...voidCombos],
                    [...items, ...voidItems],
                    subtotalCents,
                    taxCents,
                    totalCents,
                    orderType,
                    startTime,
                )
                .then((order) => {
                    if (managerId) {
                        logs.logManagerAction(
                            "cancel-order",
                            managerId,
                            stationMode,
                        );
                    }

                    dispatch(addOrder(order));
                });
        } else {
            return Promise.resolve();
        }
    };
}

export function startNewOrder(
    nextOrderType?: API.models.OrderType,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(clearCurrentOrder());
        if (nextOrderType) {
            dispatch(
                Actions.currentOrderActions.selectOrderType(nextOrderType),
            );
        }
        return dispatch(reserveCurrentOrderId()); // this action sets error state within redux, caller does not need to explicitly handle.
    };
}

export function removeOrder(id: string): AnyAction {
    return {
        type: posActionTypes.POS_ORDERS_REMOVE,
        id,
    };
}

export function setCurrentOrderCustomerInfo(
    guestName: string | null,
    pagerNumber: number | null,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_CUSTOMER_INFO,
        guestName,
        pagerNumber,
    };
}

export function setCurrentOrderStartTime(startTime: Date | null): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_START_TIME,
        startTime,
    };
}

export function setCurrentOrderEndTime(endTime: Date | null): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_END_TIME,
        endTime,
    };
}

function setCurrentOrderStartTimeIfEmpty(): AppThunk {
    return (dispatch, getState) => {
        const state = getState();
        const cartEmpty = posSelectors.getIsCurrentOrderEmpty(state);

        if (cartEmpty && !state.pos.currentOrder.startTime) {
            dispatch(setCurrentOrderStartTime(new Date()));
        }
    };
}

function resetCurrentOrderStateIfEmpty(): AppThunk {
    return (dispatch, getState) => {
        const state = getState();
        const cartEmpty = posSelectors.getIsCurrentOrderEmpty(state);

        if (cartEmpty && state.pos.currentOrder.resetOnCartEmptied) {
            dispatch(startNewOrder());
            return;
        }

        if (cartEmpty && state.pos.currentOrder.startTime) {
            dispatch(setCurrentOrderStartTime(null));
            dispatch(setAllergyWarningShown(false));
        }
    };
}

export function setCurrentOrderDriveThruDetails(
    driveThruDetails: posModels.DriveThruDetails,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_DRIVE_THRU_DETAILS,
        driveThruDetails,
    };
}

export function openPendingOrder(order: posModels.OrderParsed): AppThunk {
    return (dispatch, getState) => {
        const state = getState();
        const defaultDate = new Date();
        const items: ReduxModels.ItemCustomization[] = [];
        const combos: ReduxModels.ComboCustomization[] = [];

        for (const oc of order.combos) {
            const globalCombo = state.combos.byId[oc.comboId];
            const menuCombo =
                state.menuCombos.byMenuId[state.currentOrder.menuId ?? ""]?.[
                    oc.comboId
                ];

            if (!globalCombo || !menuCombo) {
                throw new Error(errors.PENDING_ORDER_INVALID_ENTITIES);
            }

            const comboCustomizationId = uuid4();
            const itemCustomizationIds: string[] = [];

            for (const oci of oc.items) {
                const globalItem = state.items.byId[oci.itemId];
                const menuItem =
                    state.menuItems.byMenuId[state.currentOrder.menuId ?? ""]?.[
                        oci.itemId
                    ];

                if (!globalItem || !menuItem) {
                    throw new Error(errors.PENDING_ORDER_INVALID_ENTITIES);
                }

                for (const orderItemMod of oci.modifiers) {
                    const menuItemMod =
                        state.menuItemModifiers.byMenuId[
                            state.currentOrder.menuId ?? ""
                        ]?.[oci.itemId]?.[orderItemMod.modifierId];
                    const menuItemModSelection = menuItemMod?.selections.find(
                        (s) => s.id === orderItemMod.modifierSelectionId,
                    );
                    const allPrepsMod =
                        state.pos.allPreps.byId[orderItemMod.modifierId];
                    const allPrepsModSelection = allPrepsMod?.selections.find(
                        (s) => s.id === orderItemMod.modifierSelectionId,
                    );

                    if (
                        (!menuItemMod || !menuItemModSelection) &&
                        (!allPrepsMod || !allPrepsModSelection)
                    ) {
                        throw new Error(errors.PENDING_ORDER_INVALID_ENTITIES);
                    }
                }

                const itemCustomizationId = uuid4();
                items.push({
                    comboCustomizationId: comboCustomizationId,
                    customizationId: itemCustomizationId,
                    itemId: oci.itemId,
                    quantity: 1,
                    modifierSelections: Lib.util.modsArrayToObj(oci.modifiers),
                    dateAdded: oc.dateAdded
                        ? new Date(oc.dateAdded)
                        : defaultDate, // TODO: check if this is okay since orders before these changes go live won't have dateAdded, dateModified
                    dateModified: oc.dateModified
                        ? new Date(oc.dateModified)
                        : defaultDate,
                });

                itemCustomizationIds.push(itemCustomizationId);
            }

            combos.push({
                customizationId: comboCustomizationId,
                comboId: oc.comboId,
                quantity: oc.quantity,
                itemCustomizationIds: itemCustomizationIds,
                dateAdded: new Date(oc.dateAdded),
                dateModified: new Date(oc.dateModified),
            });
        }

        for (const oi of order.items) {
            const globalItem = state.items.byId[oi.itemId];
            const menuItem =
                state.menuItems.byMenuId[state.currentOrder.menuId ?? ""]?.[
                    oi.itemId
                ];

            if (!globalItem || !menuItem) {
                throw new Error(errors.PENDING_ORDER_INVALID_ENTITIES);
            }

            for (const orderItemMod of oi.modifiers) {
                const menuItemMod =
                    state.menuItemModifiers.byMenuId[
                        state.currentOrder.menuId ?? ""
                    ]?.[oi.itemId]?.[orderItemMod.modifierId];
                const menuItemModSelection = menuItemMod?.selections.find(
                    (s) => s.id === orderItemMod.modifierSelectionId,
                );
                const allPrepsMod =
                    state.pos.allPreps.byId[orderItemMod.modifierId];
                const allPrepsModSelection = allPrepsMod?.selections.find(
                    (s) => s.id === orderItemMod.modifierSelectionId,
                );

                if (
                    (!menuItemMod || !menuItemModSelection) &&
                    (!allPrepsMod || !allPrepsModSelection)
                ) {
                    throw new Error(errors.PENDING_ORDER_INVALID_ENTITIES);
                }
            }

            items.push({
                comboCustomizationId: null,
                customizationId: uuid4(),
                itemId: oi.itemId,
                quantity: oi.quantity,
                modifierSelections: Lib.util.modsArrayToObj(oi.modifiers),
                dateAdded: oi.dateAdded ? new Date(oi.dateAdded) : defaultDate,
                dateModified: oi.dateModified
                    ? new Date(oi.dateModified)
                    : defaultDate,
            });
        }
        dispatch(clearCurrentOrder());
        dispatch(
            Actions.currentOrderActions.selectOrderType(
                order.type as API.models.OrderType,
            ),
        );
        dispatch(Actions.currentOrderActions.setItemsAndCombos(items, combos));
        if (order.charityRoundUpSelected) {
            dispatch(Actions.currentOrderActions.setShouldRoundUp(true));
        } else if (order.charityAmountCents) {
            dispatch(
                currentOrderSetCharityAmountCents(order.charityAmountCents),
            );
        }
        if (
            order.unprocessedDiscounts &&
            order.unprocessedDiscounts.length > 0
        ) {
            order.unprocessedDiscounts.forEach((d) => {
                dispatch(
                    currentOrderAddDiscount(
                        d.code,
                        d.id,
                        d.amountCents,
                        null,
                        d.type,
                    ),
                );
            });
        }
        dispatch(
            setCurrentOrderIdRefNumAndNumber(
                order.id,
                order.referenceNumber,
                order.orderNumber,
            ),
        );
        dispatch(
            setCurrentOrderCustomerInfo(
                order.guestName ?? null,
                order.pagerNumber ?? null,
            ),
        );
        dispatch(
            setCurrentOrderDriveThruDetails(
                order.driveThruDetails ?? {
                    vehicleType: "",
                    vehicleColor: "",
                    eatingInCar: false,
                    laneNumber: 1,
                },
            ),
        );
        dispatch(setCurrentOrderStartTime(order.startTime));
    };
}

export function addItem(
    itemId: string,
    quantity: number,
    modifierSelections: API.models.ModifierSelectionDict,
    groupingId?: string,
    excludeDigitalOnly?: boolean,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(setCurrentOrderStartTimeIfEmpty());

        dispatch(
            Actions.currentOrderActions.addItem(
                itemId,
                quantity,
                modifierSelections,
                groupingId,
                excludeDigitalOnly,
            ),
        );

        if (state.pos.store.autoComboEnabled) {
            dispatch(Actions.currentOrderActions.autoComboOrderItems());
        }

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());
    };
}

export function addItems(
    items: ReduxModels.NewItemCustomization[],
    combos: ReduxModels.NewComboCustomization[],
    persistCart?: boolean,
    excludeDigitalOnly?: boolean,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(setCurrentOrderStartTimeIfEmpty());

        dispatch(
            Actions.currentOrderActions.addItems(
                items,
                combos,
                persistCart,
                excludeDigitalOnly,
            ),
        );
        if (state.pos.store.autoComboEnabled) {
            dispatch(Actions.currentOrderActions.autoComboOrderItems());
        }

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());
    };
}

export function uncomboAndRemoveItem(
    comboCustomizationId: string,
    itemCustomizationIdToRemove: string,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(
            Actions.currentOrderActions.uncomboAndRemoveItem(
                comboCustomizationId,
                itemCustomizationIdToRemove,
            ),
        );
        if (state.pos.store.autoComboEnabled) {
            dispatch(Actions.currentOrderActions.autoComboOrderItems());
        }

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());

        const stationMode = state.pos.station.mode;
        logs.logGeneralAction("cart-entity-removed", stationMode);
    };
}

export function addCombo(
    comboId: string,
    quantity: number,
    items: Actions.currentOrderActions.ComboItemCustomization[],
    excludeDigitalOnly?: boolean,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(setCurrentOrderStartTimeIfEmpty());

        dispatch(
            Actions.currentOrderActions.addCombo(
                comboId,
                quantity,
                items,
                excludeDigitalOnly,
            ),
        );

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());
    };
}

export function splitItem(
    itemCustomizationId: string,
    newQuantity: number,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(
            Actions.currentOrderActions.splitItem(
                itemCustomizationId,
                newQuantity,
            ),
        );

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }
    };
}

export function splitCombo(
    comboCustomizationId: string,
    newQuantity: number,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(
            Actions.currentOrderActions.splitCombo(
                comboCustomizationId,
                newQuantity,
            ),
        );

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }
    };
}

export function modifyCustomization(
    customizationId: string,
    quantity: number,
    modifierSelections: API.models.ModifierSelectionDict,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(
            Actions.customizationActions.modifyCustomization(
                customizationId,
                quantity,
                modifierSelections,
            ),
        );

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());
    };
}

export function modifyComboCustomization(
    comboCustomizationId: string,
    quantity: number,
    items: {
        itemId: string;
        modifierSelections: API.models.ModifierSelectionDict;
    }[],
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(
            Actions.customizationActions.modifyComboCustomization(
                comboCustomizationId,
                quantity,
                items,
            ),
        );

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());
    };
}

function changeVoidedItemQuantity(id: string, quantity: number): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_VOIDED_ITEMS_CHANGE_QUANTITY,
        id,
        quantity,
    };
}

function changeVoidedComboQuantity(id: string, quantity: number): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_VOIDED_COMBOS_CHANGE_QUANTITY,
        id,
        quantity,
    };
}

export function changeCurrentOrderItemQuantity(
    customizationId: string,
    quantity: number,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(
            Actions.customizationActions.changeItemQuantity(
                customizationId,
                quantity,
            ),
        );

        const voidedItemsById = state.pos.currentOrderVoided.items.voided.byId;
        const voidId = state.pos.currentOrder.voidedItemIds.find(
            (id) => voidedItemsById[id].itemCustomizationId === customizationId,
        );
        if (voidId) {
            const voidItem = voidedItemsById[voidId];
            if (voidItem.quantity > quantity) {
                dispatch(changeVoidedItemQuantity(voidId, quantity));
            }
        }

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());
    };
}

export function changeCurrentOrderComboQuantity(
    customizationId: string,
    quantity: number,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(
            Actions.customizationActions.changeComboQuantity(
                customizationId,
                quantity,
            ),
        );

        const voidedCombosById =
            state.pos.currentOrderVoided.combos.voided.byId;
        const voidId = state.pos.currentOrder.voidedComboIds.find(
            (id) =>
                voidedCombosById[id].comboCustomizationId === customizationId,
        );
        if (voidId) {
            const voidCombo = voidedCombosById[voidId];
            if (voidCombo.quantity > quantity) {
                dispatch(changeVoidedComboQuantity(voidId, quantity));
            }
        }

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());
    };
}

export function removeCurrentOrderItem(customizationId: string): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        const numCartEntities = posSelectors.getCurrentOrderNumEntities(state);
        const hasCCPayments =
            state.pos.currentOrder.payment.creditCardsApplied.length > 0;
        const hasNonCCPayments =
            posSelectors.getCurrentOrderHasNonCCPayments(state);

        const removingLastEntity = numCartEntities === 1;

        if (removingLastEntity && (hasCCPayments || hasNonCCPayments)) {
            const errorMessage = hasCCPayments
                ? cartEntityRemovalErrorMessages.hasCCPayments
                : cartEntityRemovalErrorMessages.hasNonCCPayments;
            dispatch(setLastCartEntityRemovalErrorMessage(errorMessage));
            return;
        }

        dispatch(Actions.currentOrderActions.removeItem(customizationId));

        const voidedItemsById = state.pos.currentOrderVoided.items.voided.byId;
        const voidId = state.pos.currentOrder.voidedItemIds.find(
            (id) => voidedItemsById[id].itemCustomizationId === customizationId,
        );
        if (voidId) {
            dispatch(removeVoidedItem(voidId));
        }

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());

        dispatch(resetCurrentOrderStateIfEmpty());

        const stationMode = state.pos.station.mode;
        logs.logGeneralAction("cart-entity-removed", stationMode);
    };
}

export function removeCurrentOrderCombo(customizationId: string): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        const numCartEntities = posSelectors.getCurrentOrderNumEntities(state);
        const hasCCPayments =
            state.pos.currentOrder.payment.creditCardsApplied.length > 0;
        const hasNonCCPayments =
            posSelectors.getCurrentOrderHasNonCCPayments(state);

        const removingLastEntity = numCartEntities === 1;

        if (removingLastEntity && (hasCCPayments || hasNonCCPayments)) {
            const errorMessage = hasCCPayments
                ? cartEntityRemovalErrorMessages.hasCCPayments
                : cartEntityRemovalErrorMessages.hasNonCCPayments;
            dispatch(setLastCartEntityRemovalErrorMessage(errorMessage));
            return;
        }

        dispatch(Actions.currentOrderActions.removeCombo(customizationId));

        const voidedCombosById =
            state.pos.currentOrderVoided.combos.voided.byId;
        const voidId = state.pos.currentOrder.voidedComboIds.find(
            (id) =>
                voidedCombosById[id].comboCustomizationId === customizationId,
        );
        if (voidId) {
            dispatch(removeVoidedCombo(voidId));
        }

        if (state.pos.station.mode === "drive_thru_order_taker") {
            dispatch(updateDriveThruOrderContents());
        }

        dispatch(revalidateDiscounts());

        dispatch(resetCurrentOrderStateIfEmpty());

        const stationMode = state.pos.station.mode;
        logs.logGeneralAction("cart-entity-removed", stationMode);
    };
}

export function setLastCartEntityRemovalErrorMessage(
    lastCartEntityRemovalErrorMessage: string | undefined,
): AnyAction {
    return {
        type: posActionTypes.POS_ERRORS_SET_LAST_CART_ENTITY_REMOVAL_ERROR_MESSAGE,
        lastCartEntityRemovalErrorMessage,
    };
}

function addPendingVoidedItemImmediate(
    pendingVoidedItem: posModels.PendingVoidedItem,
): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_VOIDED_ITEM_ADD,
        pendingVoidedItem,
    };
}

export function addPendingVoidedItem(
    itemCustomizationId: string,
    quantity: number,
): AppThunk {
    return (dispatch) => {
        dispatch(
            addPendingVoidedItemImmediate({
                id: uuid4(),
                itemCustomizationId,
                quantity,
            }),
        );
    };
}

function addPendingVoidedComboImmediate(
    pendingVoidedCombo: posModels.PendingVoidedCombo,
): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_VOIDED_COMBO_ADD,
        pendingVoidedCombo,
    };
}

export function addPendingVoidedCombo(
    comboCustomizationId: string,
    quantity: number,
): AppThunk {
    return (dispatch) => {
        dispatch(
            addPendingVoidedComboImmediate({
                id: uuid4(),
                comboCustomizationId,
                quantity,
            }),
        );
    };
}

function addVoidedCombos(voidedCombos: posModels.VoidedCombo[]): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_VOIDED_COMBOS_ADD,
        voidedCombos,
    };
}

export function removeVoidedCombo(id: string): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_VOIDED_COMBOS_REMOVE,
        id,
    };
}

export function removeVoidedItem(id: string): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_VOIDED_ITEMS_REMOVE,
        id,
    };
}

function addVoidedItems(voidedItems: posModels.VoidedItem[]): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_VOIDED_ITEMS_ADD,
        voidedItems,
    };
}

function clearPendingVoidedItems(): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_VOIDED_CLEAR,
    };
}

export function voidAllPendingItems(reason: string): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        const pendingVoidedCombosById =
            state.pos.currentOrderVoided.combos.pending.byId;
        const pendingVoidedComboIds = Object.keys(pendingVoidedCombosById);
        const voidedCombos = pendingVoidedComboIds.map((id) => ({
            ...pendingVoidedCombosById[id],
            reason,
        }));

        const pendingVoidedItemsById =
            state.pos.currentOrderVoided.items.pending.byId;
        const pendingVoidedItemIds = Object.keys(pendingVoidedItemsById);
        const voidedItems = pendingVoidedItemIds.map((id) => ({
            ...pendingVoidedItemsById[id],
            reason,
        }));

        if (voidedCombos.length) {
            dispatch(addVoidedCombos(voidedCombos));
        }

        if (voidedItems.length) {
            dispatch(addVoidedItems(voidedItems));
        }

        dispatch(clearPendingVoidedItems());
    };
}

export function removePendingVoidedItem(id: string): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_VOIDED_ITEM_REMOVE,
        id,
    };
}

export function removePendingVoidedCombo(id: string): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_VOIDED_COMBO_REMOVE,
        id,
    };
}

export function changePendingVoidedItemQuantity(
    id: string,
    quantity: number,
): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_VOIDED_ITEM_CHANGE_QUANTITY,
        id,
        quantity,
    };
}

export function changePendingVoidedComboQuantity(
    id: string,
    quantity: number,
): AnyAction {
    return {
        type: posActionTypes.POS_PENDING_VOIDED_COMBO_CHANGE_QUANTITY,
        id,
        quantity,
    };
}

export function voidEntireOrder(): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        const pendingVoidedCombosById =
            state.pos.currentOrderVoided.combos.pending.byId;
        const pendingVoidedComboIds = Object.keys(pendingVoidedCombosById);
        const currentOrderComboCustomizationIds =
            state.currentOrder.comboCustomizationIds;
        currentOrderComboCustomizationIds.forEach((id) => {
            const pendingComboId = pendingVoidedComboIds.find(
                (comboId) =>
                    pendingVoidedCombosById[comboId].comboCustomizationId ===
                    id,
            );
            const customization = state.customizations.combos.byInternalId[id];
            if (pendingComboId) {
                // change quantity of existing pending-voided-combo if it doesn't match current order combo quantity
                if (
                    pendingVoidedCombosById[pendingComboId].quantity !==
                    customization.quantity
                ) {
                    dispatch(
                        changePendingVoidedComboQuantity(
                            pendingComboId,
                            customization.quantity,
                        ),
                    );
                }
            } else {
                dispatch(addPendingVoidedCombo(id, customization.quantity));
            }
        });

        const pendingVoidedItemsById =
            state.pos.currentOrderVoided.items.pending.byId;
        const pendingVoidedItemIds = Object.keys(pendingVoidedItemsById);
        const currentOrderItemCustomizationIds =
            state.currentOrder.itemCustomizationIds;
        currentOrderItemCustomizationIds.forEach((id) => {
            const pendingItemId = pendingVoidedItemIds.find(
                (itemId) =>
                    pendingVoidedItemsById[itemId].itemCustomizationId === id,
            );
            const customization = state.customizations.items.byInternalId[id];
            if (pendingItemId) {
                // change quantity of existing pending-voided-item if it doesn't match current order item quantity
                if (
                    pendingVoidedItemsById[pendingItemId].quantity !==
                    customization.quantity
                ) {
                    dispatch(
                        changePendingVoidedItemQuantity(
                            pendingItemId,
                            customization.quantity,
                        ),
                    );
                }
            } else {
                dispatch(addPendingVoidedItem(id, customization.quantity));
            }
        });
    };
}

const discountStrings = {
    discountValidationFailed:
        "The applied discount(s) could not be validated and have been removed:\n\n",
};

export function setDiscountErrorMessage(
    discountErrorMessage: string | undefined,
): AnyAction {
    return {
        type: posActionTypes.POS_ERRORS_SET_DISCOUNT_ERROR_MESSAGE,
        discountErrorMessage,
    };
}

export function revalidateDiscounts(): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        let errorMsg = "";

        const validateDiscountCalls = state.pos.currentOrder.discounts.map(
            (d) =>
                dispatch(
                    currentOrderValidateDiscount(
                        d.discountCode,
                        d.rewardId,
                        d.employeeId,
                        undefined,
                        true,
                        true,
                    ),
                ),
        );

        Promise.allSettled(validateDiscountCalls).then((validationResults) => {
            validationResults.forEach((validationResult, i) => {
                const originalDiscount = state.pos.currentOrder.discounts[i];

                if (validationResult.status === "rejected") {
                    errorMsg = !errorMsg
                        ? discountStrings.discountValidationFailed
                        : errorMsg;
                    const discountName =
                        originalDiscount.details?.name ??
                        originalDiscount.name ??
                        (originalDiscount.discountType === "tracks"
                            ? "Tracks Coupon"
                            : "unknown");
                    errorMsg += `${discountName} - ${validationResult.reason.message}\n`;

                    if (
                        validationResult.reason.message ===
                        errors.NO_DISCOUNTS_CART_EMPTY
                    ) {
                        logger.info(
                            `Removed discount with code ${
                                originalDiscount.discountCode ??
                                originalDiscount.rewardId
                            } since cart is empty`,
                        );
                    } else {
                        logger.info(
                            `Removed discount with code ${
                                originalDiscount.discountCode ??
                                originalDiscount.rewardId
                            } due to error`,
                            {errorMessage: validationResult.reason.message},
                        );
                    }
                    dispatch(
                        currentOrderRemoveDiscount(
                            originalDiscount.discountCode,
                            originalDiscount.rewardId,
                        ),
                    );
                    return;
                }

                // If validation succeeds, remove and re-add discount
                dispatch(
                    currentOrderRemoveDiscount(
                        originalDiscount.discountCode,
                        originalDiscount.rewardId,
                    ),
                );
                dispatch(
                    currentOrderAddDiscount(
                        originalDiscount.discountCode,
                        originalDiscount.rewardId,
                        validationResult.value.redemptionAmountCents,
                        validationResult.value.redemptionAmountPercentage,
                        validationResult.value.discountType,
                        originalDiscount.name,
                        originalDiscount.employeeId,
                        originalDiscount.reason,
                        originalDiscount.managerId,
                        originalDiscount.details,
                        originalDiscount.account,
                    ),
                );
            });

            if (errorMsg) {
                dispatch(setDiscountErrorMessage(errorMsg));
            }
        });
    };
}

export function currentOrderRemoveDiscountImmediate(
    discountCode: string | null,
    rewardId: string | null,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_REMOVE_DISCOUNT,
        discountCode,
        rewardId,
    };
}

export function currentOrderRemoveDiscount(
    discountCode: string | null,
    rewardId: string | null,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(currentOrderRemoveDiscountImmediate(discountCode, rewardId));

        const stationMode = state.pos.station.mode;
        logs.logGeneralAction("cart-entity-removed", stationMode);
    };
}

function currentOrderAddDiscountImmediate(
    discountCode: string | null,
    rewardId: string | null,
    amountCents: API.models.USDCents | null,
    amountPercentage: number | null,
    discountType: posModels.DiscountType | null,
    name?: string,
    employeeId?: string,
    reason?: string,
    managerId?: string,
    details?: posModels.POSDiscountDetails | null,
    account?: posModels.DiscountAccount | null,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_ADD_DISCOUNT,
        name,
        discountCode,
        rewardId,
        amountCents,
        amountPercentage,
        discountType,
        employeeId,
        reason,
        managerId,
        details,
        account,
    };
}

export function currentOrderAddDiscount(
    discountCode: string | null,
    rewardId: string | null,
    amountCents: API.models.USDCents | null,
    amountPercentage: number | null,
    discountType: posModels.DiscountType | null,
    name?: string,
    employeeId?: string,
    reason?: string,
    managerId?: string,
    details?: posModels.POSDiscountDetails | null,
    account?: posModels.DiscountAccount | null,
): AppThunk {
    return (dispatch, getState) => {
        const state = getState();
        const stationMode = state.pos.station.mode;

        dispatch(
            currentOrderAddDiscountImmediate(
                discountCode,
                rewardId,
                amountCents,
                amountPercentage,
                discountType,
                name,
                employeeId,
                reason,
                managerId,
                details,
                account,
            ),
        );

        if (discountType == "manager" && managerId) {
            logs.logManagerAction(
                "apply-manager-discount",
                managerId,
                stationMode,
            );
        }
    };
}

function getItemDiscountInfo(
    itemCustomizationIds: string[],
    itemCustomizations: {
        byInternalId: {
            [internalId: string]: ReduxModels.ItemCustomization;
        };
    },
    menuId: string | null,
    menuItemModifiersbyId: {
        [menuId: string]: {
            [itemId: string]: {
                [modifierId: string]: MenuItemModifier;
            };
        };
    },
    allPrepsModsById: {
        byId: {
            [id: string]: Lib.selectors.ItemModifierData;
        };
    },
) {
    if (!menuId) {
        return [];
    }

    const menuItemModifiers = menuItemModifiersbyId[menuId];

    const items: posModels.ItemDiscountInfo[] = itemCustomizationIds.map(
        (icId) => {
            const itemCustomization = itemCustomizations.byInternalId[icId];
            const itemId = itemCustomization.itemId;
            const quantity = itemCustomization.quantity;
            const modifierSelections = itemCustomization.modifierSelections;
            return {
                quantity: quantity,
                customization: {
                    itemId: itemId,
                    modifiers: Object.keys(modifierSelections).map(
                        (modifierId) => {
                            const menuItemModifier =
                                menuItemModifiers[itemCustomization.itemId][
                                    modifierId
                                ];
                            let modSelection:
                                | API.models.MenuItemModifierSelection
                                | Lib.selectors.ItemModifierSelectionData
                                | undefined = undefined;

                            if (menuItemModifier) {
                                const [selection] =
                                    menuItemModifier.selections.filter(
                                        (x) =>
                                            x.id ===
                                            modifierSelections[modifierId]
                                                .selectionId,
                                    );
                                modSelection = selection;
                            }

                            if (!modSelection) {
                                const allPrepMod =
                                    allPrepsModsById.byId[modifierId];
                                const allPrepModSelection =
                                    allPrepMod.selections.find(
                                        (selection) =>
                                            selection.id ===
                                            itemCustomization
                                                .modifierSelections[modifierId]
                                                ?.selectionId,
                                    );
                                modSelection = allPrepModSelection;
                            }

                            if (!modSelection) {
                                throw new Error(
                                    `modifier selection for modifier id: ${modifierId} on item could not be found`,
                                );
                            }

                            return {
                                modifierId: modifierId,
                                modifierSelectionId: modSelection.id,
                                quantity: quantity,
                            };
                        },
                    ),
                },
            };
        },
    );

    return items;
}

function getComboDiscountInfo(
    currentOrder: CurrentOrderState,
    comboCustomizations: {
        byInternalId: {
            [internalId: string]: ReduxModels.ComboCustomization;
        };
    },
    itemCustomizations: {
        byInternalId: {
            [internalId: string]: ReduxModels.ItemCustomization;
        };
    },
    menuId: string | null,
    menuItemModifiersbyId: {
        [menuId: string]: {
            [itemId: string]: {
                [modifierId: string]: MenuItemModifier;
            };
        };
    },
    allPrepsModsById: {
        byId: {
            [id: string]: Lib.selectors.ItemModifierData;
        };
    },
) {
    if (!menuId) {
        return [];
    }

    const combos: posModels.ComboDiscountInfo[] =
        currentOrder.comboCustomizationIds.map((ccId) => {
            const comboCustomization = comboCustomizations.byInternalId[ccId];
            const itemCustomizationIds =
                comboCustomization.itemCustomizationIds;
            const comboId = comboCustomization.comboId;
            const quantity = comboCustomization.quantity;
            const items = getItemDiscountInfo(
                itemCustomizationIds,
                itemCustomizations,
                menuId,
                menuItemModifiersbyId,
                allPrepsModsById,
            );
            const comboItems = items.map(
                (itemDiscountInfo) => itemDiscountInfo.customization,
            );

            return {
                comboId: comboId,
                quantity: quantity,
                items: comboItems,
            };
        });

    return combos;
}

export function currentOrderValidateDiscount(
    discountCode: string | null,
    rewardId: string | null,
    employeeId?: string,
    reason?: string,
    ignoreExistingDiscountType = false,
    noDetails = false,
): AppThunk<Promise<posModels.DiscountResponse>> {
    return async (dispatch, getState) => {
        if (!discountCode && !rewardId) {
            throw new Error(
                "One of discountCode or rewardId must be provided.",
            );
        }

        const state = getState();
        const currentOrder = state.currentOrder;
        const orderId = state.pos.currentOrder.id;
        const comboCustomizations = state.customizations.combos;
        const itemCustomizations = state.customizations.items;
        const menuId = state.currentOrder.menuId;
        const menuItemModifiersbyId = state.menuItemModifiers.byMenuId;
        const allPrepsMods = state.pos.allPreps;

        if (!orderId || !menuId) {
            throw new Error(
                `invalid current order id: ${orderId} and/or invalid menuId: ${menuId}`,
            );
        }

        const combos = getComboDiscountInfo(
            currentOrder,
            comboCustomizations,
            itemCustomizations,
            menuId,
            menuItemModifiersbyId,
            allPrepsMods,
        );
        const items = getItemDiscountInfo(
            currentOrder.itemCustomizationIds,
            itemCustomizations,
            menuId,
            menuItemModifiersbyId,
            allPrepsMods,
        );

        if (combos.length === 0 && items.length === 0) {
            // cart is empty, throw error to remove discount
            throw new Error(errors.NO_DISCOUNTS_CART_EMPTY);
        }

        let existingDiscountType = undefined;
        const currentOrderDiscounts = state.pos.currentOrder.discounts;
        if (!ignoreExistingDiscountType && currentOrderDiscounts.length > 0) {
            // either every discount in the list is a tracks discount or the length of current order discount is 1. we can just use the type of the first discount in the list
            existingDiscountType = currentOrderDiscounts[0].discountType;
        }

        const isDuplicate = !!currentOrderDiscounts.find(
            (d) => d.discountCode === discountCode,
        );

        return discountMethods
            .validate(
                discountCode,
                rewardId,
                combos,
                items,
                orderId,
                menuId,
                employeeId,
                reason,
                existingDiscountType,
                isDuplicate,
                noDetails,
            )
            .then((response) => {
                return response;
            });
    };
}

function addExistingGiftCardRequestImmediate(
    cardNumber: string,
    originalBalanceCents: API.models.USDCents,
    amountToAddCents: API.models.USDCents,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_ADD_EXISTING_GIFT_CARD_REQUEST,
        id: uuid4(),
        cardNumber,
        originalBalanceCents,
        amountToAddCents,
    };
}

export function addExistingGiftCardRequest(
    cardNumber: string,
    originalBalanceCents: API.models.USDCents,
    amountToAddCents: API.models.USDCents,
): AppThunk {
    return (dispatch) => {
        dispatch(setCurrentOrderStartTimeIfEmpty());

        dispatch(
            addExistingGiftCardRequestImmediate(
                cardNumber,
                originalBalanceCents,
                amountToAddCents,
            ),
        );
    };
}

export function updateExistingGiftCardRequest(
    id: string,
    amountToAddCents: API.models.USDCents,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_UPDATE_EXISTING_GIFT_CARD_REQUEST,
        id,
        amountToAddCents,
    };
}

function removeExistingGiftCardRequestImmediate(id: string): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_REMOVE_EXISTING_GIFT_CARD_REQUEST,
        id,
    };
}

export function removeExistingGiftCardRequest(id: string): AppThunk {
    return (dispatch, getState) => {
        const state = getState();

        const numCartEntities = posSelectors.getCurrentOrderNumEntities(state);
        const hasCCPayments =
            state.pos.currentOrder.payment.creditCardsApplied.length > 0;
        const hasNonCCPayments =
            posSelectors.getCurrentOrderHasNonCCPayments(state);

        const removingLastEntity = numCartEntities === 1;

        if (removingLastEntity && (hasCCPayments || hasNonCCPayments)) {
            const errorMessage = hasCCPayments
                ? cartEntityRemovalErrorMessages.hasCCPayments
                : cartEntityRemovalErrorMessages.hasNonCCPayments;
            dispatch(setLastCartEntityRemovalErrorMessage(errorMessage));
            return;
        }

        dispatch(removeExistingGiftCardRequestImmediate(id));

        dispatch(resetCurrentOrderStateIfEmpty());

        const stationMode = state.pos.station.mode;
        logs.logGeneralAction("cart-entity-removed", stationMode);
    };
}

export function submitExistingGiftCardRequests(): AppThunk<
    Promise<posModels.GiftCardsIncreaseBalanceResponse>
> {
    return (dispatch, getState) => {
        const state = getState();
        const orderId = state.pos.currentOrder.id;
        if (!orderId) {
            throw new Error("Current order ID is null");
        }

        const existingCards = state.pos.currentOrder.giftCards.addFunds;
        return paymentMethods.increaseGiftCardBalance(
            orderId,
            existingCards.map((gc) => ({
                cardNumber: gc.cardNumber,
                amountCents: gc.amountToAddCents,
            })),
        );
    };
}

function addNewGiftCardRequestImmediate(
    balanceCents: API.models.USDCents,
    quantity: number,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_ADD_NEW_GIFT_CARD_REQUEST,
        id: uuid4(),
        balanceCents,
        quantity,
    };
}

export function addNewGiftCardRequest(
    balanceCents: API.models.USDCents,
    quantity: number,
): AppThunk {
    return (dispatch) => {
        dispatch(setCurrentOrderStartTimeIfEmpty());

        dispatch(addNewGiftCardRequestImmediate(balanceCents, quantity));
    };
}

export function updateNewGiftCardRequest(
    id: string,
    balanceCents: API.models.USDCents,
    quantity: number,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_UPDATE_NEW_GIFT_CARD_REQUEST,
        id,
        balanceCents,
        quantity,
    };
}

function removeNewGiftCardRequestImmediate(id: string): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_REMOVE_NEW_GIFT_CARD_REQUEST,
        id,
    };
}

export function removeNewGiftCardRequest(id: string): AppThunk {
    return (dispatch, getState) => {
        const state = getState();
        const numCartEntities = posSelectors.getCurrentOrderNumEntities(state);
        const hasCCPayments =
            state.pos.currentOrder.payment.creditCardsApplied.length > 0;
        const hasNonCCPayments =
            posSelectors.getCurrentOrderHasNonCCPayments(state);

        const removingLastEntity = numCartEntities === 1;

        if (removingLastEntity && (hasCCPayments || hasNonCCPayments)) {
            const errorMessage = hasCCPayments
                ? cartEntityRemovalErrorMessages.hasCCPayments
                : cartEntityRemovalErrorMessages.hasNonCCPayments;
            dispatch(setLastCartEntityRemovalErrorMessage(errorMessage));
            return;
        }

        dispatch(removeNewGiftCardRequestImmediate(id));

        dispatch(resetCurrentOrderStateIfEmpty());

        const stationMode = state.pos.station.mode;
        logs.logGeneralAction("cart-entity-removed", stationMode);
    };
}

export function currentOrderSetQsrSelections(
    selections: posModels.QsrSelection[],
) {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_QSR_SELECTIONS,
        selections,
    };
}

function setHavePaymentsCoveredBalance(
    havePaymentsCoveredBalance: boolean,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_HAVE_PAYMENTS_COVERED_BALANCE,
        havePaymentsCoveredBalance,
    };
}

export function placeNonGiftCardPurchaseOrderIfPaymentsCover(): AppThunk<
    Promise<void>
> {
    return (dispatch, getState) => {
        const state = getState();

        const balanceDueCents =
            posSelectors.getCurrentOrderBalanceDueCents(state);
        if (balanceDueCents <= 0) {
            dispatch(setHavePaymentsCoveredBalance(true));
            if (state.pos.currentOrder.giftCards.purchase.length) {
                return Promise.resolve();
            }
            return dispatch(processPaymentAndPlaceCurrentOrder());
        }

        return Promise.resolve();
    };
}

export function requestCCPayment(
    stationMode: posModels.StationMode, // should be one of front_of_house_1, front_of_house_2, drive_thru_order_fulfillment
    taxCents: API.models.USDCents,
    totalCents: API.models.USDCents,
): AppThunk<Promise<posModels.ProcessPaymentResponse>> {
    return async (dispatch, getState) => {
        const state = getState();
        const posCurrentOrder = state.pos.currentOrder;
        const referenceNumber = posCurrentOrder.referenceNumber;
        if (referenceNumber === null) {
            throw new Error("invalid reference number on current order");
        }

        // TODO: might have to change this once we allow gc purchasing with regular orders
        const hideTipsPrompt =
            posCurrentOrder.giftCards.purchase.length > 0 ||
            posCurrentOrder.giftCards.addFunds.length > 0;

        // TODO: this might need to change as we convert to doing computation with dollar strings.
        return ccMethods
            .processPayment(
                stationMode,
                referenceNumber,
                referenceNumber,
                referenceNumber.toString(),
                totalCents,
                taxCents,
                hideTipsPrompt,
            )
            .then((response) => {
                return response;
            });
    };
}

function setPaymentCashPaidCentsImmediate(
    amountCents: API.models.USDCents,
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_SET_CASH_PAID_CENTS,
        amountCents,
    };
}

export function setPaymentCashPaidCents(
    amountCents: API.models.USDCents,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(setPaymentCashPaidCentsImmediate(amountCents));
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

function addToPaymentCashPaidCentsImmediate(
    amountCents: API.models.USDCents,
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_ADD_TO_CASH_PAID_CENTS,
        amountCents,
    };
}

export function addToPaymentCashPaidCents(
    amountCents: API.models.USDCents,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(addToPaymentCashPaidCentsImmediate(amountCents));
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

export function setPaymentChangeOwedCents(
    amountCents: API.models.USDCents,
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_SET_CHANGE_OWED_CENTS,
        amountCents,
    };
}

function applyPaymentGiftCardImmediate(
    giftCard: posModels.AppliedGiftCard,
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_APPLY_GIFT_CARD,
        giftCard,
    };
}

export function applyPaymentGiftCard(
    giftCard: posModels.AppliedGiftCard,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(applyPaymentGiftCardImmediate(giftCard));
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

export function removePaymentGiftCard(id: string): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_REMOVE_GIFT_CARD,
        id,
    };
}

function setPaymentGiftCardsImmediate(
    giftCardsApplied: posModels.AppliedGiftCard[],
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_SET_GIFT_CARDS,
        giftCardsApplied,
    };
}

export function setPaymentGiftCards(
    giftCardsApplied: posModels.AppliedGiftCard[],
): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(setPaymentGiftCardsImmediate(giftCardsApplied));
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

function applyPaymentCompCardImmediate(
    compCard: posModels.AppliedCompCard,
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_APPLY_COMP_CARD,
        compCard,
    };
}

export function applyPaymentCompCard(
    compCard: posModels.AppliedCompCard,
    remainingBalanceOnceApplied: number,
): AppThunk<Promise<void>> {
    return (dispatch, getState) => {
        const state = getState();
        const currentGiftCardsApplied =
            state.pos.currentOrder.payment.giftCardsApplied;
        if (remainingBalanceOnceApplied <= 0) {
            dispatch(setPaymentGiftCardsImmediate([]));
        } else {
            const newAppliedGiftCards: posModels.AppliedGiftCard[] = [];
            let remainingBalance = remainingBalanceOnceApplied;
            for (const appliedGiftCard of currentGiftCardsApplied) {
                const appliedCents = Math.min(
                    appliedGiftCard.appliedCents,
                    remainingBalance,
                );
                const newAppliedGiftCard = {
                    ...appliedGiftCard,
                    appliedCents: appliedCents,
                };
                newAppliedGiftCards.push(newAppliedGiftCard);
                remainingBalance -= appliedCents;
                if (remainingBalance <= 0) {
                    break;
                }
            }
            dispatch(setPaymentGiftCardsImmediate(newAppliedGiftCards));
        }
        dispatch(applyPaymentCompCardImmediate(compCard));
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

export function removePaymentCompCard(id: string): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_REMOVE_COMP_CARD,
        id,
    };
}

function applyPaymentCreditCardImmediate(
    creditCard: posModels.AppliedCreditCard,
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_ADD_CREDIT_CARD,
        creditCard,
    };
}

export function applyPaymentCreditCard(
    creditCard: posModels.AppliedCreditCard,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(applyPaymentCreditCardImmediate(creditCard));
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

function applyEzCaterImmediate(): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_APPLY_EZ_CATER,
    };
}

export function applyEzCater(): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(applyEzCaterImmediate());
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

export function applyExternalTenderImmediate(
    tender: posModels.AppliedExternalTender,
): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_APPLY_EXTERNAL_TENDER,
        tender,
    };
}

export function applyExternalTender(
    tender: posModels.AppliedExternalTender,
): AppThunk<Promise<void>> {
    return (dispatch) => {
        dispatch(applyExternalTenderImmediate(tender));
        return dispatch(placeNonGiftCardPurchaseOrderIfPaymentsCover());
    };
}

export function removeExternalTender(tenderName: string): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_REMOVE_EXTERNAL_TENDER,
        tenderName,
    };
}

export function setCurrentOrderEzCaterOrderType(
    ezCaterOrderType: "pickup" | "delivery" | undefined,
) {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_EZ_CATER_ORDER_TYPE,
        ezCaterOrderType,
    };
}

function setPosStoreSettingsImmediate(
    id: string | null,
    penniesUnavailable: boolean,
    kioskCashOrderPagerNumber: number | null,
    charitySettings: posModels.CharitySettings,
    supportedExternalTenders: posModels.ExternalTender[],
    isLab: boolean,
    taxExemptEnabled: boolean,
    autoComboEnabled: boolean,
    paidInsOutsEnabled: boolean,
): AnyAction {
    return {
        type: posActionTypes.POS_SET_STORE_SETTINGS,
        id,
        penniesUnavailable,
        kioskCashOrderPagerNumber,
        charitySettings,
        supportedExternalTenders,
        isLab,
        taxExemptEnabled,
        autoComboEnabled,
        paidInsOutsEnabled,
    };
}

function setPosAppIsParallelImmediate(isParallel: boolean) {
    return {
        type: posActionTypes.POS_SET_APP_IS_PARALLEL,
        isParallel,
    };
}

export function loadStoreSettings(): AppThunk<
    Promise<posModels.StoreSettings>
> {
    return async (dispatch) =>
        stores.getStoreSettings().then((storeSettings) => {
            // Initialize store settings
            dispatch(
                setPosStoreSettingsImmediate(
                    storeSettings.storeId,
                    storeSettings.penniesUnavailable ?? false,
                    storeSettings.kioskCashOrderPagerNumber,
                    storeSettings.charitySettings,
                    storeSettings.externalTenders ?? [],
                    storeSettings.isLab,
                    storeSettings.taxExemptEnabled ?? false,
                    storeSettings.autoComboEnabled,
                    storeSettings.paidInsOutsEnabled,
                ),
            );
            dispatch(
                setPosAppIsParallelImmediate(storeSettings.isParallelMode),
            );

            // Initialize store address and tax rate
            const storeInfo = {
                id: storeSettings.storeId,
                taxRate: storeSettings.taxRate,
                address: storeSettings.address,
                established: "n/a",
                name: "n/a",
                status: Constants.storeStatus.ONLINE,
                storeNumber: storeSettings.storeNumber,
                maxOrderCost: 500,
                maxOrderCostCents: 50000,
                supportsCurbside: false,
                supportsCharlane: false,
                curbsideAutoCheckIn: {enabled: false},
                supportsDineIn: true,
                hours: {
                    driveThru: [],
                    inStore: [],
                    modified: {
                        driveThru: [],
                        inStore: [],
                    },
                },
                images: [],
                supportsQrToGo: false,
                supportsQrDineIn: false,
                isItemComboUpsellEnabled: false,
                supportsPickupLane: false,
                maxGiftCardCashOutCents: storeSettings.maxGiftCardCashOutCents,
                isCondimentUpsellEnabled: false,
                fundraiserSupportEnabled: false,
            };
            dispatch(Actions.storeActions.setStores([storeInfo]));

            return storeSettings;
        });
}

function updateStoreSettingsImmediate(
    penniesUnavailable: boolean,
    kioskCashOrderPagerNumber: number | null,
) {
    return {
        type: posActionTypes.POS_UPDATE_STORE_SETTINGS,
        penniesUnavailable,
        kioskCashOrderPagerNumber,
    };
}

export function updateStoreSettings(
    penniesUnavailable: boolean,
    kioskCashOrderPagerNumber: number | null,
): AppThunk<Promise<void>> {
    return async (dispatch) => {
        return stores
            .updateStoreSettings(penniesUnavailable, kioskCashOrderPagerNumber)
            .then(() => {
                dispatch(
                    updateStoreSettingsImmediate(
                        penniesUnavailable,
                        kioskCashOrderPagerNumber,
                    ),
                );
            });
    };
}

export function clearPayments(): AnyAction {
    return {
        type: posActionTypes.POS_PAYMENT_CLEAR,
    };
}

export function currentOrderSetCharityAmountCents(
    charityAmountCents: API.models.USDCents | null,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_CHARITY_AMOUNT_CENTS,
        charityAmountCents,
    };
}

export function setCurrentOrderIsTaxExempt(isTaxExempt: boolean): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_IS_TAX_EXEMPT,
        isTaxExempt,
    };
}

export function setAllergyWarningShown(
    allergyWarningShown: boolean,
): AnyAction {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_ALLERGY_WARNING_SHOWN,
        allergyWarningShown,
    };
}

export function setResetCurrentOrderStateOnCartEmptied(
    resetOnCartEmptied: boolean,
) {
    return {
        type: posActionTypes.POS_CURRENT_ORDER_SET_RESET_ON_CART_EMPTIED,
        resetOnCartEmptied,
    };
}

export function setPlaceOrderAfterPaymentProcessedErrored(
    placeOrderAfterPaymentsProcessedErrored: boolean,
) {
    return {
        type: posActionTypes.POS_ERRORS_SET_PLACE_ORDER_AFTER_PAYMENTS_PROCESSED_ERRORED,
        placeOrderAfterPaymentsProcessedErrored,
    };
}

function setAssignedTillImmediate(assignedTill: number): AnyAction {
    return {
        type: posActionTypes.POS_STATION_SET_ASSIGNED_TILL,
        assignedTill,
    };
}

function setTrainingModeAssignedTillImmediate(
    trainingModeAssignedTill: number,
): AnyAction {
    return {
        type: posActionTypes.POS_STATION_SET_TRAINING_MODE_ASSIGNED_TILL,
        trainingModeAssignedTill,
    };
}

function setAssigningTill(isAssigningTill: boolean): AnyAction {
    return {
        type: posActionTypes.POS_STATION_SET_ASSIGNING_TILL,
        isAssigningTill,
    };
}

export function setTillAssignmentSkipped(
    tillAssignmentSkipped: boolean,
): AnyAction {
    return {
        type: posActionTypes.POS_STATION_SET_TILL_ASSIGNMENT_SKIPPED,
        tillAssignmentSkipped,
    };
}

export function setTillAssignmentErrorMessage(
    tillAssignmentErrorMessage: string | undefined,
): AnyAction {
    return {
        type: posActionTypes.POS_ERRORS_SET_TILL_ASSIGNMENT_ERROR_MESSAGE,
        tillAssignmentErrorMessage,
    };
}

const tillAssignmentStrings = {
    cashDrawerError:
        "An unexpected error occurred when establishing a connection with the cash drawer.",
    cashDrawerErrorWithMessage: (errMessage: string) =>
        `There was an error related to the cash drawer: ${errMessage}`,
    skipAssignmentErrored: "Failed to skip till assignment. Please try again.",
};

export function assignTill(
    assignedTill: number,
    modifyLocalStateOnly = false,
): AppThunk<Promise<void>> {
    return (dispatch, getState) => {
        const state = getState();
        const deviceId = state.pos.deviceId;
        const isTraining = state.pos.station.isTraining;

        if (isTraining) {
            dispatch(setTrainingModeAssignedTillImmediate(assignedTill));
            return Promise.resolve();
        }

        if (modifyLocalStateOnly) {
            dispatch(setAssignedTillImmediate(assignedTill));
            return Promise.resolve();
        }

        dispatch(setAssigningTill(true));
        // if we can't open the till that is connected, we shouldn't get to a state which makes it seem like we have a successful assignment.
        return openCashDrawer()
            .then(() => {
                return till.logTillAssignment(deviceId ?? "", assignedTill);
            })
            .then(() => {
                dispatch(setAssignedTillImmediate(assignedTill));
            })
            .catch((err) => {
                let errMsg = "";
                if (!err.config) {
                    // not an axios error, cash drawer error
                    if (err.message) {
                        errMsg =
                            tillAssignmentStrings.cashDrawerErrorWithMessage(
                                err.message,
                            );
                    } else {
                        errMsg = tillAssignmentStrings.cashDrawerError;
                    }
                    Sentry.captureMessage(errMsg);
                } else {
                    errMsg = describeNetworkError(err).join("\n");
                    logger.warn("Failed to log till assignment.", {
                        errorMessage: errMsg,
                    });
                }
                dispatch(setTillAssignmentErrorMessage(errMsg));
            })
            .finally(() => {
                dispatch(setAssigningTill(false));
            });
    };
}

export function selectNoTillOverride(
    managerId: string,
): AppThunk<Promise<void>> {
    return (dispatch, getState) => {
        const state = getState();
        const deviceId = state.pos.deviceId;
        const stationMode = state.pos.station.mode;
        dispatch(setAssigningTill(true));

        return till
            .logTillAssignment(deviceId ?? "", -1)
            .then(() => {
                logs.logManagerAction(
                    "no-till-override",
                    managerId,
                    stationMode,
                );

                dispatch(setTillAssignmentSkipped(true));
            })
            .catch(() => {
                logger.warn("Failed to skip till assignment.");
                dispatch(
                    setTillAssignmentErrorMessage(
                        tillAssignmentStrings.skipAssignmentErrored,
                    ),
                );
            })
            .finally(() => {
                dispatch(setAssigningTill(false));
            });
    };
}
